import { checkSession, signOut, type ChallengeType } from '@/lib/auth';
import { toTitleCase } from '@/utils/common';
import { delay } from '@/utils/delay';
import { createWithDevtools } from './utils';

export interface User {
  email: string;
  firstname?: string;
  lastname?: string;
}

export interface AuthStateProps {
  user: User | null;
  email: string;
  session: string;
  isLoading: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
  isLogoutDialogVisible: boolean;
  challengeType?: ChallengeType;
}

export interface AuthStateActions {
  setEmail: (email: string) => void;
  setUser: (user: User | null) => void;
  setSession: (session: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setChallengeType: (challengeType: ChallengeType) => void;
  showLogoutConfirmationDialog: () => void;
  hideLogoutConfirmationDialog: () => void;
  logoutSession: () => Promise<void>;
  loadSession: () => Promise<void>;
  clearAuth: () => void;
}

export type AuthState = AuthStateProps & AuthStateActions;

export const initialState: AuthStateProps = {
  user: null,
  email: '',
  session: '',
  isLoading: true,
  isInitialized: false,
  isAuthenticated: false,
  isLogoutDialogVisible: false,
  challengeType: undefined,
};

export const useAuthStore = createWithDevtools<AuthState>(
  set => ({
    ...initialState,
    setUser: user => {
      set({ user, isAuthenticated: !!user });
    },
    setEmail: email => {
      set({ email });
    },
    setSession: session => {
      set({ session });
    },
    setIsLoading: isLoading => {
      set({ isLoading });
    },
    setChallengeType: challengeType => {
      set({ challengeType });
    },
    clearAuth: () => {
      set({ ...initialState, isLoading: false, isInitialized: true });
    },
    loadSession: async (): Promise<void> => {
      set({ isLoading: true });
      const session = await checkSession();

      if (session) {
        set({ user: session, isLoading: false, isInitialized: true, isAuthenticated: true });
        return;
      }

      set({ user: null, isLoading: false, isInitialized: true, isAuthenticated: false });
    },
    showLogoutConfirmationDialog: () => {
      set({ isLogoutDialogVisible: true });
    },
    hideLogoutConfirmationDialog: () => {
      set({ isLogoutDialogVisible: false });
    },
    logoutSession: async (): Promise<void> => {
      set({ isLoading: true });

      try {
        await signOut();
      } catch (error) {
        console.error(error);
      }

      await delay(500); // Delay here is for smoother UX/animations.
      //  Force clear the user session, even if there are any errors.
      set({ ...initialState, isLoading: false, isInitialized: true });
    },
  }),
  'AuthStore',
);

export const selectUserEmail = (state: AuthState) => {
  return state.user?.email ?? state?.email ?? '';
};

export const selectUserFirstname = (state: AuthState) => {
  return toTitleCase(state.user?.firstname ?? '').trim();
};

export const selectUserLastname = (state: AuthState) => {
  return toTitleCase(state.user?.lastname ?? '').trim();
};

export const selectUserFullname = (state: AuthState) => {
  return `${selectUserFirstname(state)} ${selectUserLastname(state)}`.trim();
};

// Only to be used within test files.
export const authStore = useAuthStore;
